import { RSU } from '_lib/api';
import { themeSelector } from '_store/application/selectors';
import devicesReducer from '_store/devices/devicesReducers';
import {
  devicesSelector,
  rsuDowntimesDataSelector,
  rsuDowntimesErrorSelector,
  rsuDowntimesLoadingSelector,
} from '_store/devices/selectors';
import { setHeatmapActiveAction } from '_store/heatmap/heatmapReducers';
import { heatmapSelector } from '_store/heatmap/selectors';
import { roadsSelector } from '_store/roads/selectors';
import { ReactLocalization, useLocalization } from '@fluent/react';
import { faFileArrowDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  CardContent,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Tooltip as MuiTooltip,
  Typography,
} from '@material-ui/core';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import { addDays, subDays } from 'date-fns';
import React, { useEffect, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useDispatch, useSelector } from 'react-redux';

import { CheckboxItem } from '../../items/CheckboxItem';
import DatePicker from '../../items/DatePicker';
import MultiSelectItem from '../../items/MultiSelectItem';
import RsuMultiSelect from '../../items/RsuMultiSelect';
import SelectItem from '../../items/SelectItem';
import { HeatmapControls } from './HeatmapControls';
import styles from './styles';

const showHeatmapFeature = process.env.SHOW_HEATMAP !== 'false';
type DashboardPanel = 'rsu' | 'heatmap';

interface DashboardProps {
  heatmapPanelCollapsed?: boolean;
  onHeatmapCollapseToggle?: () => void;
  /** Right edge of the panel (px from viewport left) for positioning the collapse button */
  panelRightEdge?: number | null;
}

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

enum PeriodType {
  MONTH = 'month',
  INTERVAL = 'interval',
}

const getMonths = (l10n: ReactLocalization) => [
    l10n.getString('months-january'),
    l10n.getString('months-february'),
    l10n.getString('months-march'),
    l10n.getString('months-april'),
    l10n.getString('months-may'),
    l10n.getString('months-june'),
    l10n.getString('months-july'),
    l10n.getString('months-august'),
    l10n.getString('months-september'),
    l10n.getString('months-october'),
    l10n.getString('months-november'),
    l10n.getString('months-december'),
  ];

// Charts options
const createOptions = (title: string, l10n: ReactLocalization, isDarkTheme: boolean) => ({
  responsive: true,
  plugins: {
    legend: {
      position: 'top' as const,
      labels: {
        color: isDarkTheme ? '#ffffff' : '#666666',
        font: {
          size: 12,
        },
      },
    },
    title: {
      display: true,
      text: title,
      color: isDarkTheme ? '#ffffff' : '#666666',
      font: {
        size: 16,
        weight: 'bold' as const,
      },
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      title: {
        display: true,
        text: l10n.getString('dashboard-rsu-downtimes-chart-y-axis'),
        color: isDarkTheme ? '#ffffff' : '#666666',
      },
      ticks: {
        color: isDarkTheme ? '#ffffff' : '#666666',
      },
      grid: {
        color: isDarkTheme ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
      },
    },
    x: {
      title: {
        display: true,
        text: l10n.getString('dashboard-rsu-downtimes-chart-x-axis'),
        color: isDarkTheme ? '#ffffff' : '#666666',
      },
      ticks: {
        color: isDarkTheme ? '#ffffff' : '#666666',
      },
      grid: {
        color: isDarkTheme ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
      },
    },
  },
});

// Function to generate all dates between start and end (inclusive)
const generateDateRange = (startDate: string, endDate: string): string[] => {
  const dates: string[] = [];
  const start = new Date(startDate);
  const end = new Date(endDate);

  // Set time to midnight to avoid timezone issues
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);

  const current = new Date(start);
  while (current <= end) {
    const year = current.getFullYear();
    const month = (current.getMonth() + 1).toString().padStart(2, '0');
    const day = current.getDate().toString().padStart(2, '0');
    dates.push(`${year}-${month}-${day}`);
    current.setDate(current.getDate() + 1);
  }

  return dates;
};

// Function to convert API data to chart data
const convertApiDataToChartData = (
  apiData: any,
  selectedRoads: string[],
  startDate: string,
  endDate: string,
  selectedRsuIds?: string[],
  devices?: RSU[],
  roads?: Array<{ id: string; name: string }>,
) => {
  // Generate all dates in the selected range (not just dates from API)
  // This ensures we always show all dates, even when no data is returned
  const sortedDates = generateDateRange(startDate, endDate);
  const labels = sortedDates.map((date) => {
    const d = new Date(date);
    return `${d.getDate().toString().padStart(2, '0')}-${(d.getMonth() + 1)
      .toString()
      .padStart(2, '0')}`;
  });

  const colors = [
    'rgba(255, 99, 132, 0.8)',
    'rgba(54, 162, 235, 0.8)',
    'rgba(255, 206, 86, 0.8)',
    'rgba(75, 192, 192, 0.8)',
    'rgba(153, 102, 255, 0.8)',
    'rgba(255, 159, 64, 0.8)',
    'rgba(199, 199, 199, 0.8)',
    'rgba(83, 102, 255, 0.8)',
    'rgba(255, 99, 255, 0.8)',
    'rgba(99, 255, 132, 0.8)',
    'rgba(255, 255, 0, 0.8)',
    'rgba(255, 128, 0, 0.8)',
    'rgba(0, 255, 255, 0.8)',
    'rgba(128, 0, 128, 0.8)',
    'rgba(255, 20, 147, 0.8)',
    'rgba(0, 128, 0, 0.8)',
    'rgba(255, 69, 0, 0.8)',
    'rgba(0, 191, 255, 0.8)',
    'rgba(138, 43, 226, 0.8)',
    'rgba(50, 205, 50, 0.8)',
  ];

  // If no segments returned from API, return datasets with all zeros
  if (!apiData?.segments || apiData.segments.length === 0) {
    // Get road names from roads parameter if available, otherwise use roadId
    const roadNameMap = new Map<string, string>();
    if (roads) {
      roads.forEach((road) => {
        if (selectedRoads.includes(road.id)) {
          roadNameMap.set(road.id, road.name);
        }
      });
    }

    // If specific RSUs are selected, show individual RSU lines with zeros
    if (selectedRsuIds && selectedRsuIds.length > 0) {
      const datasets: any[] = [];
      let colorIndex = 0;

      selectedRsuIds.forEach((rsuId) => {
        const device = devices?.find((d) => d.id === rsuId);
        if (device && selectedRoads.includes(device.roadSegmentId)) {
          const zeroData = sortedDates.map(() => 0);
          datasets.push({
            label: device.name,
            data: zeroData,
            borderColor: colors[colorIndex % colors.length],
            backgroundColor: colors[colorIndex % colors.length].replace('0.8', '0.3'),
            tension: 0.3,
          });
          colorIndex++;
        }
      });

      return { labels, datasets };
    }

    // If no specific RSUs selected, show aggregated data by road segment with zeros
    const datasets = selectedRoads.map((roadId, index) => {
      const roadName = roadNameMap.get(roadId) || roadId;
      const zeroData = sortedDates.map(() => 0);

      return {
        label: roadName,
        data: zeroData,
        borderColor: colors[index % colors.length],
        backgroundColor: colors[index % colors.length].replace('0.8', '0.3'),
        tension: 0.3,
      };
    });

    return { labels, datasets };
  }

  // Create a map from RSU ID to name for filtering
  const rsuIdToNameMap = new Map<string, string>();
  if (devices) {
    devices.forEach((device) => {
      rsuIdToNameMap.set(device.id, device.name);
    });
  }

  // If specific RSUs are selected, show individual RSU lines
  if (selectedRsuIds && selectedRsuIds.length > 0) {
    const datasets: any[] = [];
    let colorIndex = 0;

    selectedRoads.forEach((roadId) => {
      const segment = apiData.segments.find((s: any) => s.roadSegmentId === roadId);
      if (!segment) {
        // If segment not found but RSU is selected, show zeros for that RSU
        const selectedDevicesInRoad = devices?.filter(
          (d) => d.roadSegmentId === roadId && selectedRsuIds.includes(d.id),
        );
        selectedDevicesInRoad?.forEach((device) => {
          const zeroData = sortedDates.map(() => 0);
          datasets.push({
            label: device.name,
            data: zeroData,
            borderColor: colors[colorIndex % colors.length],
            backgroundColor: colors[colorIndex % colors.length].replace('0.8', '0.3'),
            tension: 0.3,
          });
          colorIndex++;
        });
        return;
      }

      segment.devices.forEach((device: any) => {
        // Check if this device name matches any selected RSU ID
        const isSelected = selectedRsuIds.some((rsuId) => {
          const rsuName = rsuIdToNameMap.get(rsuId);
          return rsuName === device.name;
        });

        if (isSelected) {
          const data = sortedDates.map((date) => {
            const dayData = device.days.find((d: any) => d.date === date);
            return dayData ? dayData.downtime / 3600 : 0; // Convert seconds to hours
          });

          datasets.push({
            label: device.name,
            data,
            borderColor: colors[colorIndex % colors.length],
            backgroundColor: colors[colorIndex % colors.length].replace('0.8', '0.3'),
            tension: 0.3,
          });
          colorIndex++;
        }
      });
    });

    return { labels, datasets };
  }

  // If no specific RSUs selected, show aggregated data by road segment
  const datasets = selectedRoads.map((roadId, index) => {
    const segment = apiData.segments.find((s: any) => s.roadSegmentId === roadId);
    const roadName = segment ? segment.roadSegmentName : roadId;

    const data = sortedDates.map((date) => {
      if (!segment) return 0;

      let totalDowntimeSeconds = 0;
      segment.devices.forEach((device: any) => {
        const dayData = device.days.find((d: any) => d.date === date);
        if (dayData) {
          totalDowntimeSeconds += dayData.downtime;
        }
      });

      return totalDowntimeSeconds / 3600; // Convert seconds to hours
    });

    return {
      label: roadName,
      data,
      borderColor: colors[index % colors.length],
      backgroundColor: colors[index % colors.length].replace('0.8', '0.3'),
      tension: 0.3,
    };
  });

  return { labels, datasets };
};

const FULL_DAY_SECONDS = 86400;

/**
 * Injects synthetic device entries (24 h/day downtime) for every RSU that appears in
 * the Redux device list but is absent from `unfilteredSnapshot`. These are devices that
 * were created offline and never sent a single diagnostic, so the API has no record of them.
 *
 * Works both when `apiData` is the full unfiltered response and when it is a per-RSU
 * filtered response – in the latter case the offline devices are still injected so the
 * chart can display their 24 h/day line when they are explicitly selected.
 */
function enrichWithOfflineDevices(
  apiData: any,
  allDevices: RSU[],
  unfilteredSnapshot: any,
  selectedRoadIds: string[],
  allRoads: Array<{ id: string; name: string }>,
  dateRange: { start: string; end: string },
): any {
  const allDates = generateDateRange(dateRange.start, dateRange.end);
  const numDays = allDates.length;

  const getOfflineDevices = (roadId: string): RSU[] => {
    const devicesInRoad = allDevices.filter((d) => d.roadSegmentId === roadId);
    const snapshotSegment = unfilteredSnapshot?.segments?.find(
      (s: any) => s.roadSegmentId === roadId,
    );
    const knownNames = new Set((snapshotSegment?.devices ?? []).map((d: any) => d.name));
    return devicesInRoad.filter((d) => !knownNames.has(d.name));
  };

  const makeSyntheticDevice = (d: RSU) => ({
    name: d.name,
    rsuId: d.id,
    days: allDates.map((date) => ({ date, downtime: FULL_DAY_SECONDS })),
    totalDowntimeSeconds: numDays * FULL_DAY_SECONDS,
    totalDowntimeHours: numDays * 24,
  });

  if (!apiData?.segments) {
    const segments = selectedRoadIds
      .map((roadId) => {
        const offlineDevices = getOfflineDevices(roadId);
        if (offlineDevices.length === 0) return null;
        const road = allRoads.find((r) => r.id === roadId);
        const syntheticDevices = offlineDevices.map(makeSyntheticDevice);
        const totalDowntimeSeconds = numDays * FULL_DAY_SECONDS * syntheticDevices.length;
        return {
          roadSegmentId: roadId,
          roadSegmentName: road?.name ?? roadId,
          devices: syntheticDevices,
          totalDevicesDowntimeSeconds: totalDowntimeSeconds,
          totalDevicesDowntimeHours: totalDowntimeSeconds / 3600,
        };
      })
      .filter(Boolean);
    if (segments.length === 0) return apiData;
    return { ...(apiData ?? {}), segments };
  }

  let changed = false;
  const enrichedSegments = apiData.segments.map((segment: any) => {
    const offlineDevices = getOfflineDevices(segment.roadSegmentId);
    if (offlineDevices.length === 0) return segment;
    changed = true;
    const syntheticDevices = offlineDevices.map(makeSyntheticDevice);
    const newDevices = [...(segment.devices ?? []), ...syntheticDevices];
    const totalDowntimeSeconds = newDevices.reduce(
      (sum: number, d: any) => sum + d.totalDowntimeSeconds,
      0,
    );
    return {
      ...segment,
      devices: newDevices,
      totalDevicesDowntimeSeconds: totalDowntimeSeconds,
      totalDevicesDowntimeHours: totalDowntimeSeconds / 3600,
    };
  });

  // Handle road segments present in selectedRoadIds but missing from apiData.segments
  selectedRoadIds.forEach((roadId) => {
    if (apiData.segments.some((s: any) => s.roadSegmentId === roadId)) return;
    const offlineDevices = getOfflineDevices(roadId);
    if (offlineDevices.length === 0) return;
    changed = true;
    const road = allRoads.find((r) => r.id === roadId);
    const syntheticDevices = offlineDevices.map(makeSyntheticDevice);
    const totalDowntimeSeconds = numDays * FULL_DAY_SECONDS * syntheticDevices.length;
    enrichedSegments.push({
      roadSegmentId: roadId,
      roadSegmentName: road?.name ?? roadId,
      devices: syntheticDevices,
      totalDevicesDowntimeSeconds: totalDowntimeSeconds,
      totalDevicesDowntimeHours: totalDowntimeSeconds / 3600,
    });
  });

  if (!changed) return apiData;
  return { ...apiData, segments: enrichedSegments };
}

// Function to get available years (current year and 10 years back)
const getAvailableYears = () => {
  const currentYear = new Date().getFullYear();
  const years = [];

  for (let i = 0; i <= 10; i++) {
    years.push(currentYear - i);
  }

  return years;
};

// Function to get available months for a specific year
const getAvailableMonths = (l10n: ReactLocalization, year?: number) => {
  const months = getMonths(l10n);
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  const targetYear = year || currentYear;

  const availableMonths = [];

  if (targetYear === currentYear) {
    // For current year, show months up to the current month
    for (let i = currentMonth; i >= 0; i--) {
      availableMonths.push(months[i]);
    }
  } else {
    // For previous years, show all months
    for (let i = 11; i >= 0; i--) {
      availableMonths.push(months[i]);
    }
  }

  return availableMonths;
};

// Function to convert multiple months to date range
// (first day of first month to last day of last month, or yesterday if last month is current month)
const getDateRangeFromMonths = (months: string[], l10n: ReactLocalization, year?: number) => {
  if (months.length === 0) return { start: '', end: '' };

  const monthNames = getMonths(l10n);
  const targetYear = year || new Date().getFullYear();
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();

  // Sort months by their index
  const sortedMonths = months.sort((a, b) => {
    const indexA = monthNames.indexOf(a);
    const indexB = monthNames.indexOf(b);
    return indexA - indexB;
  });

  const firstMonthIndex = monthNames.indexOf(sortedMonths[0]);
  const lastMonthIndex = monthNames.indexOf(sortedMonths[sortedMonths.length - 1]);

  const startDate = new Date(targetYear, firstMonthIndex, 1);

  // If the last month selected is the current month of the current year,
  // use yesterday as the end date. Otherwise, use the last day of the month.
  let endDate = new Date(targetYear, lastMonthIndex + 1, 0);
  if (targetYear === currentYear && lastMonthIndex === currentMonth) {
    endDate = new Date();
    endDate.setDate(endDate.getDate() - 1); // Yesterday
  }

  const formatDate = (date: Date) => {
    const dateYear = date.getFullYear();
    const dateMonth = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${dateYear}-${dateMonth}-${day}`;
  };

  return {
    start: formatDate(startDate),
    end: formatDate(endDate),
  };
};

// Function to get available months for multi-select with consecutive logic
const getAvailableMonthsForMultiSelect = (
  l10n: ReactLocalization,
  year?: number,
  selectedMonths: string[] = [],
): Array<{ value: string; label: string; disabled: boolean }> => {
  const months = getMonths(l10n);
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  const targetYear = year || currentYear;

  const availableMonths: string[] =
    targetYear === currentYear
      ? months.slice(0, currentMonth + 1).reverse()
      : months.slice().reverse();

  // If no months selected, all available months are selectable
  if (selectedMonths.length === 0) {
    return availableMonths.map((month) => ({ value: month, label: month, disabled: false }));
  }

  // If 3 months already selected, disable all others
  if (selectedMonths.length >= 3) {
    return availableMonths.map((month) => ({
      value: month,
      label: month,
      disabled: !selectedMonths.includes(month),
    }));
  }

  // For 1-2 selected months, find consecutive options
  const monthIndices = selectedMonths.map((month) => months.indexOf(month)).sort((a, b) => a - b);
  const minIndex = Math.min(...monthIndices);
  const maxIndex = Math.max(...monthIndices);

  // Check if current selection is consecutive
  const isConsecutive = monthIndices.every(
    (index, i) => i === 0 || index === monthIndices[i - 1] + 1,
  );

  return availableMonths.map((month) => {
    const monthIndex = months.indexOf(month);
    let disabled = false;

    if (selectedMonths.includes(month)) {
      disabled = false; // Always allow deselecting
    } else if (selectedMonths.length >= 3) {
      disabled = true; // Max 3 months
    } else if (!isConsecutive) {
      // If current selection is not consecutive, only allow adjacent months
      disabled = !monthIndices.some((index) => Math.abs(index - monthIndex) === 1);
    } else {
      // If current selection is consecutive, allow only adjacent months
      disabled = !(monthIndex === minIndex - 1 || monthIndex === maxIndex + 1);
    }

    return { value: month, label: month, disabled };
  });
};

export const Dashboard = ({
  heatmapPanelCollapsed = false,
  onHeatmapCollapseToggle,
  panelRightEdge = null,
}: DashboardProps): JSX.Element => {
  const dispatch = useDispatch();
  const { l10n } = useLocalization();
  const classes = styles();
  // States for filters
  const [selectedRoadsSegments, setSelectedRoadsSegments] = useState<string[]>([]);
  const [selectedRsuIds, setSelectedRsuIds] = useState<string[]>([]);
  const [periodType, setPeriodType] = useState<PeriodType>(PeriodType.MONTH);
  const [selectedMonths, setSelectedMonths] = useState<string[] | null>(null);
  const [tempSelectedMonths, setTempSelectedMonths] = useState<string[] | null>(null);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [dateFromError, setDateFromError] = useState<string | null>(null);
  const [dateToError, setDateToError] = useState<string | null>(null);
  const [dashboardPanel, setDashboardPanel] = useState<DashboardPanel>('rsu');
  // Snapshot of unfiltered downtime data used to keep per-RSU hours visible in the
  // dropdown even after the user selects a specific RSU (which triggers a filtered request)
  const [allRsuDowntimesData, setAllRsuDowntimesData] = useState<any>(null);

  // Selector to get data from store
  const roads = useSelector(roadsSelector);
  const devices = useSelector(devicesSelector);
  const rsuDowntimesData = useSelector(rsuDowntimesDataSelector);
  const rsuDowntimesLoading = useSelector(rsuDowntimesLoadingSelector);
  const rsuDowntimesError = useSelector(rsuDowntimesErrorSelector);
  const theme = useSelector(themeSelector);
  const heatmap = useSelector(heatmapSelector);
  const availableYears = getAvailableYears();
  const availableMonths = getAvailableMonths(l10n, selectedYear);

  // State for chart data
  const [dataAvailability, setDataAvailability] = useState({
    labels: [],
    datasets: [],
  });

  // Hide heatmap on the map when user is not on the Heatmap panel; default to counter when opening Heatmap panel
  useEffect(() => {
    if (!showHeatmapFeature) return;
    if (dashboardPanel === 'rsu') {
      dispatch(setHeatmapActiveAction('inactive'));
    } else if (dashboardPanel === 'heatmap' && heatmap.active === 'inactive') {
      dispatch(setHeatmapActiveAction('counter'));
    }
  }, [showHeatmapFeature, dashboardPanel, heatmap.active, dispatch]);

  // Hide heatmap when Dashboard panel is closed (X or another menu item)
  useEffect(() => {
    if (!showHeatmapFeature) return;
    return () => {
      dispatch(setHeatmapActiveAction('inactive'));
    };
  }, [showHeatmapFeature, dispatch]);

  // Load devices if not already loaded
  useEffect(() => {
    if (devices.length === 0 && roads.length > 0) {
      dispatch(
        devicesReducer.actions.devicesRequest({ roadSegmentIds: roads.map((road) => road.id) }),
      );
    }
  }, [devices.length, roads, dispatch]);

  // Initialize default values
  useEffect(() => {
    if (availableMonths.length > 0 && selectedMonths === null) {
      setSelectedMonths([availableMonths[0]]);
    }
    if (roads.length > 0 && selectedRoadsSegments.length === 0) {
      setSelectedRoadsSegments([roads[0].id]);
    }
  }, [roads, availableMonths, devices]);

  // Reset selected months when year changes to the first available month
  useEffect(() => {
    if (availableMonths.length > 0) {
      setSelectedMonths([availableMonths[0]]);
    }
  }, [selectedYear]);

  // Helper function to validate date
  const isValidDate = (date: Date | null): boolean => date !== null && date instanceof Date && !Number.isNaN(date.getTime());

  // Derive the current start/end date strings from filter state (mirrors logic in useEffects below)
  const currentDateRange = useMemo(() => {
    if (periodType === PeriodType.MONTH && selectedMonths && selectedMonths.length > 0) {
      return getDateRangeFromMonths(selectedMonths, l10n, selectedYear);
    }
    if (
      periodType === PeriodType.INTERVAL &&
      dateFrom &&
      dateTo &&
      isValidDate(dateFrom) &&
      isValidDate(dateTo)
    ) {
      const [start] = dateFrom.toISOString().split('T');
      const [end] = dateTo.toISOString().split('T');
      return { start, end };
    }
    return null;
  }, [periodType, selectedMonths, selectedYear, dateFrom, dateTo, l10n]);

  // Filter RSUs based on selected road segments, enriched with total downtime hours.
  // Uses the unfiltered snapshot so hours remain visible when a specific RSU is selected.
  // Devices absent from the API data were never online → show max downtime (24 h × number of days).
  const availableRsuOptions = devices
    .filter((device) => selectedRoadsSegments.includes(device.roadSegmentId))
    .map((device) => {
      let downtimeHours: number | undefined;
      const downtimeSource = allRsuDowntimesData ?? rsuDowntimesData;
      if (downtimeSource?.segments) {
        const segment = downtimeSource.segments.find(
          (s: any) => s.roadSegmentId === device.roadSegmentId,
        );
        const apiDevice = segment?.devices?.find((d: any) => d.name === device.name);
        if (apiDevice?.totalDowntimeHours !== undefined) {
          downtimeHours = apiDevice.totalDowntimeHours;
        }
      }
      // Device not in API data → was never online, assign maximum downtime for the period
      if (downtimeHours === undefined && currentDateRange) {
        downtimeHours =
          generateDateRange(currentDateRange.start, currentDateRange.end).length * 24;
      }
      return { id: device.id, name: device.name, downtimeHours };
    });

  // Current API data enriched with offline RSUs (24 h/day). Used for the chart.
  // Uses allRsuDowntimesData as the "oracle" for which RSUs are offline, so we don't
  // accidentally treat online-but-unselected RSUs as offline during filtered requests.
  const enrichedChartApiData = useMemo(() => {
    if (!rsuDowntimesData || !currentDateRange || !allRsuDowntimesData) return rsuDowntimesData;
    return enrichWithOfflineDevices(
      rsuDowntimesData,
      devices,
      allRsuDowntimesData,
      selectedRoadsSegments,
      roads,
      currentDateRange,
    );
  }, [rsuDowntimesData, devices, allRsuDowntimesData, selectedRoadsSegments, roads, currentDateRange]);

  // Unfiltered data enriched with offline RSUs. Used for the dropdown totals, summary cards, and CSV.
  const enrichedUnfilteredData = useMemo(() => {
    const base = allRsuDowntimesData ?? rsuDowntimesData;
    if (!base || !currentDateRange) return base;
    return enrichWithOfflineDevices(base, devices, base, selectedRoadsSegments, roads, currentDateRange);
  }, [allRsuDowntimesData, rsuDowntimesData, devices, selectedRoadsSegments, roads, currentDateRange]);

  // Clear selected RSUs when road segments change and they're no longer available
  useEffect(() => {
    if (selectedRsuIds.length > 0) {
      const availableRsuIds = availableRsuOptions.map((rsu) => rsu.id);
      const filteredSelectedRsuIds = selectedRsuIds.filter((rsuId) =>
        availableRsuIds.includes(rsuId),
      );

      if (filteredSelectedRsuIds.length !== selectedRsuIds.length) {
        setSelectedRsuIds(filteredSelectedRsuIds);
      }
    }
  }, [selectedRoadsSegments, devices, selectedRsuIds, availableRsuOptions]);

  // Capture unfiltered downtime snapshot so per-RSU hours remain visible in the
  // dropdown while the user has a specific RSU selected (filtered request replaces store data)
  useEffect(() => {
    if (selectedRsuIds.length === 0 && rsuDowntimesData) {
      setAllRsuDowntimesData(rsuDowntimesData);
    }
  }, [rsuDowntimesData, selectedRsuIds]);

  // Reset the snapshot when the primary date/road filters change so stale hours are not shown
  useEffect(() => {
    setAllRsuDowntimesData(null);
  }, [selectedRoadsSegments, periodType, selectedMonths, selectedYear, dateFrom, dateTo]);

  // Update data when filters change
  useEffect(() => {
    if (selectedRoadsSegments.length > 0) {
      let start: string;
      let end: string;

      if (periodType === PeriodType.MONTH && selectedMonths && selectedMonths.length > 0) {
        const dateRange = getDateRangeFromMonths(selectedMonths, l10n, selectedYear);
        start = dateRange.start;
        end = dateRange.end;
      } else if (periodType === PeriodType.INTERVAL && dateFrom && dateTo) {
        // Validate dates before using them
        if (!isValidDate(dateFrom) || !isValidDate(dateTo)) {
          return; // Don't fetch if dates are invalid
        }
        [start] = dateFrom.toISOString().split('T');
        [end] = dateTo.toISOString().split('T');
      } else {
        // If no months selected in month mode, don't fetch data
        if (periodType === PeriodType.MONTH && (!selectedMonths || selectedMonths.length === 0)) {
          return;
        }
        // If interval mode but no dates selected, don't fetch data
        if (periodType === PeriodType.INTERVAL && (!dateFrom || !dateTo)) {
          return;
        }
        return;
      }

      dispatch(
        devicesReducer.actions.rsuDowntimesRequest({
          start,
          end,
          roadSegmentIds: selectedRoadsSegments,
          rsuIds: selectedRsuIds.length > 0 ? selectedRsuIds : undefined,
        }),
      );
    }
  }, [
    selectedRoadsSegments,
    selectedRsuIds,
    periodType,
    selectedMonths,
    selectedYear,
    dateFrom,
    dateTo,
    dispatch,
  ]);

  // Update chart when data from API arrives or when filters change
  // This ensures chart is updated even when no data is returned (showing zeros)
  useEffect(() => {
    if (selectedRoadsSegments.length > 0) {
      let start: string;
      let end: string;

      if (periodType === PeriodType.MONTH && selectedMonths && selectedMonths.length > 0) {
        const dateRange = getDateRangeFromMonths(selectedMonths, l10n, selectedYear);
        start = dateRange.start;
        end = dateRange.end;
      } else if (periodType === PeriodType.INTERVAL && dateFrom && dateTo) {
        // Validate dates before using them
        if (!isValidDate(dateFrom) || !isValidDate(dateTo)) {
          return; // Don't update chart if dates are invalid
        }
        [start] = dateFrom.toISOString().split('T');
        [end] = dateTo.toISOString().split('T');
      } else {
        // Don't update chart if no valid date range
        return;
      }

      // Generate chart data even if rsuDowntimesData is null/undefined (will show zeros).
      // Use enrichedChartApiData so offline RSUs are rendered with 24 h/day downtime.
      const chartData = convertApiDataToChartData(
        enrichedChartApiData || null,
        selectedRoadsSegments,
        start,
        end,
        selectedRsuIds,
        devices,
        roads,
      );
      setDataAvailability(chartData);
    }
  }, [
    enrichedChartApiData,
    selectedRoadsSegments,
    selectedRsuIds,
    devices,
    roads,
    periodType,
    selectedMonths,
    selectedYear,
    dateFrom,
    dateTo,
    l10n,
  ]);

  // Handle checkbox change for road selection
  const handleRoadCheckboxChange = (roadId: string) => {
    if (selectedRoadsSegments.includes(roadId)) {
      setSelectedRoadsSegments(selectedRoadsSegments.filter((id) => id !== roadId));
    } else {
      setSelectedRoadsSegments([...selectedRoadsSegments, roadId]);
    }
  };

  // Validate month selection to ensure max 3 consecutive months
  const validateMonthSelection = (
    newMonths: string[],
    previousMonths?: string[] | null,
  ): string[] => {
    if (newMonths.length === 0) {
      return [];
    }

    const months = getMonths(l10n);
    const monthIndices = newMonths
      .map((month) => months.indexOf(month))
      .filter((index) => index !== -1)
      .sort((a, b) => a - b);

    if (monthIndices.length === 0) {
      return [];
    }

    // Maximum 3 months
    if (monthIndices.length > 3) {
      // Keep only the first 3 consecutive months
      return monthIndices.slice(0, 3).map((index) => months[index]);
    }

    // Check if months are consecutive
    const isConsecutive = monthIndices.every(
      (index, i) => i === 0 || index === monthIndices[i - 1] + 1,
    );

    if (!isConsecutive) {
      // Use provided previousMonths or fall back to selectedMonths
      const prevMonths = previousMonths !== undefined ? previousMonths : selectedMonths;
      const previousIndices = (prevMonths || [])
        .map((month) => months.indexOf(month))
        .filter((index) => index !== -1)
        .sort((a, b) => a - b);

      // If user is deselecting (new selection is smaller), allow it
      if (newMonths.length < (prevMonths?.length || 0)) {
        // Allow deselection, but ensure remaining months are consecutive
        const remainingIndices = monthIndices;
        const remainingIsConsecutive = remainingIndices.every(
          (index, i) => i === 0 || index === remainingIndices[i - 1] + 1,
        );
        if (remainingIsConsecutive) {
          return newMonths;
        }
        // If remaining is not consecutive, revert to previous
        return prevMonths || [];
      }

      // User is trying to add a non-consecutive month
      // Find which month was just added
      const addedIndices = monthIndices.filter((idx) => !previousIndices.includes(idx));

      if (addedIndices.length === 1) {
        const addedIndex = addedIndices[0];
        const minPrev = previousIndices.length > 0 ? Math.min(...previousIndices) : addedIndex;
        const maxPrev = previousIndices.length > 0 ? Math.max(...previousIndices) : addedIndex;

        // Check if the added month is adjacent to the previous selection
        if (addedIndex === minPrev - 1 || addedIndex === maxPrev + 1) {
          // Valid adjacent month, combine with previous
          const combinedIndices = [...previousIndices, addedIndex].sort((a, b) => a - b);
          // Check if combined is consecutive
          const combinedIsConsecutive = combinedIndices.every(
            (index, i) => i === 0 || index === combinedIndices[i - 1] + 1,
          );
          if (combinedIsConsecutive && combinedIndices.length <= 3) {
            return combinedIndices.map((index) => months[index]);
          }
        }
      }

      // Invalid selection, revert to previous
      return prevMonths || [];
    }

    // Valid consecutive selection
    return newMonths;
  };

  // Handle month dropdown open - initialize temporary state to empty
  const handleMonthDropdownOpen = () => {
    // Initialize temporary state to empty array so all months are selectable
    setTempSelectedMonths([]);
  };

  // Handle month dropdown close - update real state from temporary state
  const handleMonthDropdownClose = () => {
    // Update the real state with the temporary selection
    const finalSelection = tempSelectedMonths || [];
    if (finalSelection.length === 0) {
      // If nothing was selected, keep the current selection (don't change it)
      setTempSelectedMonths(null);
      return;
    }
    // Validate and update the real state
    const validatedMonths = validateMonthSelection(finalSelection);
    setSelectedMonths(validatedMonths);
    // Clear temporary state
    setTempSelectedMonths(null);
  };

  // Handle month selection change with validation - update temporary state only
  const handleMonthChange = (e: React.ChangeEvent<{ name?: string; value: any }>) => {
    const newMonths = Array.isArray(e.target.value) ? e.target.value : [e.target.value];
    // Validate against current temporary state (or empty if just opened)
    const validatedMonths = validateMonthSelection(newMonths, tempSelectedMonths);
    setTempSelectedMonths(validatedMonths);
  };

  // Handle date changes with validation
  const handleDateFromChange = (date: Date | null) => {
    // Clear error when date becomes null (user is clearing)
    if (date === null) {
      setDateFrom(null);
      setDateFromError(null);
      return;
    }

    // Validate date
    if (!isValidDate(date)) {
      setDateFromError('Please enter a valid date');
      return;
    }

    // Check min/max constraints
    const maxDate = dateTo ? subDays(dateTo, 1) : subDays(new Date(), 1);
    if (date > maxDate) {
      setDateFromError(
        dateTo
          ? 'Date must be at least one day before the "to" date'
          : 'Date cannot be in the future',
      );
      return;
    }

    // Date is valid, clear error and update
    setDateFromError(null);
    setDateFrom(date);

    // If "to" date exists and is now invalid (same day or before "from"), adjust it
    // "to" must be at least one day after "from"
    if (dateTo && isValidDate(dateTo)) {
      const normalizedFrom = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const normalizedTo = new Date(dateTo.getFullYear(), dateTo.getMonth(), dateTo.getDate());
      if (normalizedTo <= normalizedFrom) {
        const newDateTo = addDays(normalizedFrom, 1);
        if (newDateTo <= subDays(new Date(), 1)) {
          setDateTo(newDateTo);
          setDateToError(null);
        }
      }
    }
  };

  const handleDateToChange = (date: Date | null) => {
    // Clear error when date becomes null (user is clearing)
    if (date === null) {
      setDateTo(null);
      setDateToError(null);
      return;
    }

    // Validate date
    if (!isValidDate(date)) {
      setDateToError('Please enter a valid date');
      return;
    }

    // Check min/max constraints
    const maxDate = subDays(new Date(), 1);
    if (date > maxDate) {
      setDateToError('Date cannot be today or in the future');
      return;
    }

    if (dateFrom && isValidDate(dateFrom)) {
      const normalizedFrom = new Date(
        dateFrom.getFullYear(),
        dateFrom.getMonth(),
        dateFrom.getDate(),
      );
      const normalizedTo = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      if (normalizedTo <= normalizedFrom) {
        setDateToError('Date must be at least one day after the "from" date');
        return;
      }
    }

    // Date is valid, clear error and update
    setDateToError(null);
    setDateTo(date);

    // If "from" date exists and is now invalid (same day or after "to"), adjust it
    // "from" must be at least one day before "to"
    if (dateFrom && isValidDate(dateFrom)) {
      const normalizedFrom = new Date(
        dateFrom.getFullYear(),
        dateFrom.getMonth(),
        dateFrom.getDate(),
      );
      const normalizedTo = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      if (normalizedFrom >= normalizedTo) {
        const newDateFrom = subDays(normalizedTo, 1);
        setDateFrom(newDateFrom);
        setDateFromError(null);
      }
    }
  };

  return (
    <CardContent className={classes.cardContent} style={{ position: 'relative' }}>
      {showHeatmapFeature && dashboardPanel === 'heatmap' && !heatmapPanelCollapsed && onHeatmapCollapseToggle && (
        <MuiTooltip title={l10n.getString('dashboard-heatmap-collapse-panel') ?? 'Collapse panel'} placement="left">
            <IconButton
              onClick={onHeatmapCollapseToggle}
              size="small"
              aria-label="Collapse heatmap panel"
              style={{
                position: 'fixed',
                left: panelRightEdge != null ? panelRightEdge + 4 : undefined,
                right: panelRightEdge == null ? 16 : undefined,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 1003,
                backgroundColor: 'rgba(255,255,255,0.9)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
              }}
            >
              <ChevronLeft />
            </IconButton>
        </MuiTooltip>
      )}
      <Box className={classes.chartContainer} component="div">
        {showHeatmapFeature && (
          <Box mb={2} display="flex" alignItems="center" flexWrap="wrap" gap={2}>
            <Typography variant="body2" color="textSecondary" component="span">
              {l10n.getString('dashboard-panel-select') ?? 'Visualizza:'}
            </Typography>
            <FormControl size="small" variant="outlined" style={{ minWidth: 220 }}>
              <Select
                value={dashboardPanel}
                onChange={(e) => setDashboardPanel(e.target.value as DashboardPanel)}
                displayEmpty
              >
                <MenuItem value="rsu">
                  {l10n.getString('dashboard-panel-rsu-downtime') ?? 'RSU downtime'}
                </MenuItem>
                <MenuItem value="heatmap">
                  {l10n.getString('dashboard-panel-heatmap') ?? 'Heatmap'}
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}
        {(!showHeatmapFeature || dashboardPanel === 'rsu') && (
          <>
            <h2>{l10n.getString('dashboard-rsu-downtimes-chart-title')}</h2>
            <p>{l10n.getString('dashboard-rsu-downtimes-chart-description')}</p>

            {/* Filters */}
            <div className={classes.filterContainer}>
          <div className={classes.filterGroup}>
            <div className={classes.filterLabel}>
              {l10n.getString('dashboard-rsu-downtimes-road-segment')}
            </div>
            <div>
              {roads.map((road) => (
                <CheckboxItem
                  key={road.id}
                  checked={selectedRoadsSegments.includes(road.id)}
                  itemId={road.id}
                  handleCheckedChange={handleRoadCheckboxChange}
                  label={road.name}
                  labelPlacement="end"
                />
              ))}
            </div>
          </div>

          <div className={classes.filterPeriodContainer}>
            <div className={classes.filterGroup}>
              <div className={classes.filterLabel}>
                {l10n.getString('dashboard-rsu-downtimes-period')}
              </div>
              <SelectItem
                text=""
                value={periodType}
                options={[
                  {
                    value: PeriodType.MONTH,
                    label: l10n.getString('dashboard-rsu-downtimes-period-month'),
                  },
                  {
                    value: PeriodType.INTERVAL,
                    label: l10n.getString('dashboard-rsu-downtimes-period-interval'),
                  },
                ]}
                onChange={(e) => setPeriodType(e.target.value as PeriodType)}
                tooltip={l10n.getString('dashboard-rsu-downtimes-period-tooltip')}
                tooltipPlacement="top"
                style={{ marginTop: 0 }}
              />
            </div>

            {periodType === PeriodType.MONTH && (
              <>
                <div className={classes.filterGroup}>
                  <div className={classes.filterLabel}>
                    {l10n.getString('dashboard-rsu-downtimes-year')}
                  </div>
                  <SelectItem
                    text=""
                    value={selectedYear}
                    options={availableYears.map((year) => ({
                      value: year,
                      label: year.toString(),
                    }))}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
                    tooltip={l10n.getString('dashboard-rsu-downtimes-year-tooltip')}
                    tooltipPlacement="top"
                    style={{ marginTop: 0 }}
                  />
                </div>
                <div className={classes.filterGroup}>
                  <div className={classes.filterLabel}>
                    {l10n.getString('dashboard-rsu-downtimes-period-month')}
                  </div>
                  <MultiSelectItem
                    text=""
                    value={
                      tempSelectedMonths !== null ? tempSelectedMonths || [] : selectedMonths || []
                    }
                    options={getAvailableMonthsForMultiSelect(
                      l10n,
                      selectedYear,
                      tempSelectedMonths !== null ? tempSelectedMonths || [] : selectedMonths || [],
                    )}
                    onChange={handleMonthChange}
                    onOpen={handleMonthDropdownOpen}
                    onClose={handleMonthDropdownClose}
                    tooltip={l10n.getString('dashboard-rsu-downtimes-period-month-tooltip')}
                    tooltipPlacement="top"
                    style={{ marginTop: 0 }}
                  />
                </div>
              </>
            )}

            {periodType === PeriodType.INTERVAL && (
              <>
                <div className={classes.filterGroup}>
                  <div className={classes.filterLabel}>
                    {l10n.getString('dashboard-rsu-downtimes-period-interval-from')}
                  </div>
                  <DatePicker
                    label=""
                    value={dateFrom}
                    onChange={handleDateFromChange}
                    maxDate={dateTo ? subDays(dateTo, 1) : subDays(new Date(), 1)}
                    disablePast={false}
                    disabled={false}
                    error={!!dateFromError}
                    helperText={dateFromError || undefined}
                    style={{ marginTop: 16 }}
                  />
                </div>
                <div className={classes.filterGroup}>
                  <div className={classes.filterLabel}>
                    {l10n.getString('dashboard-rsu-downtimes-period-interval-to')}
                  </div>
                  <DatePicker
                    label=""
                    value={dateTo}
                    onChange={handleDateToChange}
                    maxDate={subDays(new Date(), 1)}
                    minDate={dateFrom ? addDays(dateFrom, 1) : undefined}
                    disablePast={false}
                    disabled={false}
                    error={!!dateToError}
                    helperText={dateToError || undefined}
                    style={{ marginTop: 16 }}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Export CSV Button */}
        {enrichedUnfilteredData &&
          selectedRoadsSegments.length > 0 &&
          ((periodType === PeriodType.MONTH && selectedMonths && selectedMonths.length > 0) ||
            (periodType === PeriodType.INTERVAL && dateFrom && dateTo)) && (
            <div
              style={{
                marginBottom: '20px',
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <Button
                variant="contained"
                color="primary"
                startIcon={<FontAwesomeIcon icon={faFileArrowDown} />}
                onClick={() => {
                  if (!enrichedUnfilteredData) return;

                  // Generate CSV client-side so offline RSUs (24 h/day) are included
                  const rows: string[] = ['Road Segment,Device Name,Date,Downtime (h)'];
                  enrichedUnfilteredData.segments?.forEach((segment: any) => {
                    const segName = segment.roadSegmentName ?? segment.roadSegmentId;
                    segment.devices?.forEach((device: any) => {
                      device.days?.forEach((day: any) => {
                        const hours = (day.downtime / 3600).toFixed(2);
                        rows.push(`"${segName}","${device.name}","${day.date}",${hours}`);
                      });
                    });
                  });

                  const csv = rows.join('\n');
                  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                  const link = document.createElement('a');
                  const urlBlob = URL.createObjectURL(blob);
                  link.setAttribute('href', urlBlob);

                  const now = new Date();
                  const timestamp = `${now.getFullYear()}${(now.getMonth() + 1).toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}_${now.getHours().toString().padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}${now.getSeconds().toString().padStart(2, '0')}`;
                  link.setAttribute('download', `rsu_downtimes_export_${timestamp}.csv`);

                  link.style.visibility = 'hidden';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  URL.revokeObjectURL(urlBlob);
                }}
              >
                {l10n.getString('download-rsu-downtimes-csv')}
              </Button>
            </div>
          )}

        {/* RSU Filter for Chart Display Only */}
        {rsuDowntimesData &&
          selectedRoadsSegments.length > 0 &&
          ((periodType === PeriodType.MONTH && selectedMonths && selectedMonths.length > 0) ||
            (periodType === PeriodType.INTERVAL && dateFrom && dateTo)) && (
            <div className={classes.filterGroup} style={{ marginBottom: '20px' }}>
              <div className={classes.filterLabel}>
                {l10n.getString('dashboard-rsu-downtimes-rsu-filter')}
              </div>
              <RsuMultiSelect
                options={availableRsuOptions}
                selectedIds={selectedRsuIds}
                onChange={setSelectedRsuIds}
                placeholder={l10n.getString('dashboard-rsu-downtimes-rsu-placeholder')}
                tooltip={l10n.getString('dashboard-rsu-downtimes-rsu-tooltip')}
              />
            </div>
          )}

        {/* Loading and error states */}
        {rsuDowntimesLoading && (
          <div className={classes.loading}>
            {l10n.getString('dashboard-rsu-downtimes-road-segment')}
          </div>
        )}

        {rsuDowntimesError && (
          <div className={classes.error}>
            {l10n.getString('dashboard-rsu-downtimes-road-segment')}:{' '}
            {Array.isArray(rsuDowntimesError) ? rsuDowntimesError.join(', ') : rsuDowntimesError}
          </div>
        )}

        {/* Chart */}
        {(() => {
          const hasValidSelection =
            (periodType === PeriodType.MONTH && selectedMonths && selectedMonths.length > 0) ||
            (periodType === PeriodType.INTERVAL && dateFrom && dateTo);

          if (hasValidSelection) {
            return (
              <Line
                options={createOptions(
                  l10n.getString('dashboard-rsu-downtimes-road-segment'),
                  l10n,
                  theme === 'dark',
                )}
                data={dataAvailability}
              />
            );
          }

          if (periodType === PeriodType.MONTH && (!selectedMonths || selectedMonths.length === 0)) {
            return (
              <div
                style={{
                  textAlign: 'center',
                  padding: '40px',
                  color: theme === 'dark' ? '#ffffff' : '#666666',
                  fontSize: '16px',
                }}
              >
                {l10n.getString('dashboard-rsu-downtimes-select-months-message')}
              </div>
            );
          }

          return null;
        })()}

        {/* Summary of total downtime per segment */}
        {enrichedUnfilteredData?.segments &&
          selectedRoadsSegments.length > 0 &&
          ((periodType === PeriodType.MONTH && selectedMonths && selectedMonths.length > 0) ||
            (periodType === PeriodType.INTERVAL && dateFrom && dateTo)) && (
            <Box mt={4}>
              <Typography variant="h6" gutterBottom>
                {l10n.getString('dashboard-rsu-downtimes-summary-title') ||
                  'Riepilogo Downtime per Segmento'}
              </Typography>
              <Grid container spacing={2}>
                {selectedRoadsSegments.map((roadId) => {
                  const segment = enrichedUnfilteredData.segments.find(
                    (s: any) => s.roadSegmentId === roadId,
                  );
                  if (!segment) return null;

                  // Filter devices by selected RSU (offline RSUs included via enrichment)
                  const filteredDevices =
                    selectedRsuIds.length > 0
                      ? segment.devices.filter((device: any) =>
                          selectedRsuIds.some((rsuId) => {
                            const rsuName = devices.find((d) => d.id === rsuId)?.name;
                            return rsuName === device.name;
                          })
                        )
                      : segment.devices;

                  // Calculate total downtime for filtered devices
                  const totalDowntimeHours = filteredDevices.reduce(
                    (total: number, device: any) => total + device.totalDowntimeHours,
                    0,
                  );

                  return (
                    <Grid item xs={12} sm={6} md={4} key={roadId}>
                      <Paper elevation={2} style={{ padding: '16px' }}>
                        <Typography variant="h6" gutterBottom>
                          {segment.roadSegmentName}
                        </Typography>
                        <Box>
                          <Box display="flex" justifyContent="space-between" mb={1}>
                            <Typography variant="body2" color="textSecondary">
                              {l10n.getString('dashboard-rsu-downtimes-total-downtime')}
                            </Typography>
                            <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                              {totalDowntimeHours.toFixed(2)}
                            </Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="body2" color="textSecondary">
                              {l10n.getString('dashboard-rsu-downtimes-device-count')}
                            </Typography>
                            <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                              {filteredDevices.length}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}
            </>
        )}
        {showHeatmapFeature && dashboardPanel === 'heatmap' && (
          <>
            <h2>{l10n.getString('dashboard-heatmap-title') ?? 'Heatmap'}</h2>
            <p>{l10n.getString('dashboard-heatmap-description') ?? 'Visualize heatmap data on the map. Choose the metric and time range below.'}</p>
            <HeatmapControls />
          </>
        )}
      </Box>
    </CardContent>
  );
};

export default Dashboard;
